/**
 * Contact Page
 * Contact form (placeholder for now)
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiMail, FiArrowRight, FiCalendar, FiExternalLink, FiCloud, FiCpu, FiGitBranch, FiCode, FiShield, FiUsers } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../services/api';
import useEditableContent from '../hooks/useEditableContent';
import EditableModal from '../components/EditableModal';
import RichTextEditor from '../components/RichTextEditor';
import analytics from '../utils/analytics';

const CALCOM_URL = 'https://cal.plotka.me/rplotka';

const contactMethods = [
  {
    icon: FiCalendar,
    label: 'Book a Call',
    value: 'Free 30-min discovery call',
    href: `${CALCOM_URL}/discovery-call`,
    external: true,
  },
  {
    icon: FiMail,
    label: 'Email',
    value: 'rick@tsi400.com',
    href: 'mailto:rick@tsi400.com',
  },
  {
    icon: FiExternalLink,
    label: 'Consulting',
    value: 'tsi400.com',
    href: 'https://tsi400.com',
    external: true,
  },
];

const expertise = [
  { icon: FiCloud, title: 'Cloud Architecture', desc: 'GCP, AWS, infrastructure design, migration strategies, and cost optimization' },
  { icon: FiCpu, title: 'AI/ML Integration', desc: 'LLM pipelines, automation systems, intelligent agents, and data workflows' },
  { icon: FiGitBranch, title: 'DevOps & CI/CD', desc: 'GitHub Actions, Docker, Terraform, monitoring, and deployment automation' },
  { icon: FiCode, title: 'Full-Stack Development', desc: 'TypeScript, React, Node.js, PostgreSQL, and modern web architectures' },
  { icon: FiShield, title: 'Infrastructure & Security', desc: 'Zero-trust networking, secrets management, TLS, and compliance' },
  { icon: FiUsers, title: 'Technical Leadership', desc: 'Team building, architecture reviews, technical hiring, and mentorship' },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  // Contact info hook (for admin editing)
  const {
    showModal: showContactModal,
    isCreating: isCreatingContact,
    formData: contactForm,
    updateFormData: updateContactForm,
    cancelEdit: cancelContactEdit,
    save: saveContact
  } = useEditableContent('contact-info', {
    page: 'contact',
    section: 'info'
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/contact', formData);
      toast.success('Thank you for your message! I\'ll get back to you soon.');
      analytics.contactFormSubmit();
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      console.error('Contact submission failed', err);
      toast.error('Failed to send message. Please try again later.');
    }
  };

  return (
    <ContactContainer>
      <Container>
        {/* Consulting Hero */}
        <HeroSection as={motion.section} {...fadeUp} transition={{ duration: 0.6 }}>
          <Badge>TSI &mdash; Technology Strategy &amp; Innovation</Badge>
          <HeroTitle>Build better technology,<br />make smarter decisions.</HeroTitle>
          <HeroSubtitle>
            Two decades of hands-on experience in cloud architecture, AI systems, and technical leadership &mdash;
            available to help you solve your toughest challenges.
          </HeroSubtitle>
          <ConsultingCTA href={`${CALCOM_URL}/discovery-call`} target="_blank" rel="noopener noreferrer">
            Book a Free Discovery Call <FiArrowRight />
          </ConsultingCTA>
        </HeroSection>

        {/* Contact Form Section */}
        <Section as={motion.section} {...fadeUp} transition={{ duration: 0.6, delay: 0.2 }}>
          <SectionTitle>Get in Touch</SectionTitle>
        </Section>

        <ContentGrid>
          <ContactInfo
            as={motion.div}
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <InfoList>
              {contactMethods.map((method) => (
                <ContactMethodLink
                  key={method.label}
                  href={method.href}
                  target={method.external ? '_blank' : undefined}
                  rel={method.external ? 'noopener noreferrer' : undefined}
                >
                  <IconWrapper>
                    <method.icon />
                  </IconWrapper>
                  <div>
                    <InfoLabel>{method.label}</InfoLabel>
                    <InfoValue>{method.value}</InfoValue>
                  </div>
                </ContactMethodLink>
              ))}
            </InfoList>
          </ContactInfo>

          <ContactForm
            as={motion.form}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            onSubmit={handleSubmit}
          >
            <FormGroup>
              <Label htmlFor="name">Name</Label>
              <Input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Your name"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="your.email@example.com"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="subject">Subject</Label>
              <Input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                placeholder="Project inquiry"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="message">Message</Label>
              <TextArea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="6"
                placeholder="Tell me about your project..."
              />
            </FormGroup>

            <SubmitButton type="submit">Send Message</SubmitButton>
          </ContactForm>
        </ContentGrid>

        {/* Expertise */}
        <Section as={motion.section} {...fadeUp} transition={{ duration: 0.6, delay: 0.4 }}>
          <SectionTitle>Expertise</SectionTitle>
          <ExpertiseGrid>
            {expertise.map((item, i) => (
              <ExpertiseCard
                key={item.title}
                as={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + i * 0.08 }}
              >
                <ExpertiseIcon><item.icon size={24} /></ExpertiseIcon>
                <ExpertiseTitle>{item.title}</ExpertiseTitle>
                <ExpertiseDesc>{item.desc}</ExpertiseDesc>
              </ExpertiseCard>
            ))}
          </ExpertiseGrid>
        </Section>

        {/* Bottom CTA */}
        <CTACard as={motion.section} {...fadeUp} transition={{ duration: 0.6, delay: 0.6 }}>
          <SectionTitle>Ready to get started?</SectionTitle>
          <CTAText>
            Book a free 30-minute discovery call. No pitch, no pressure &mdash;
            just a conversation about your goals and how I can help.
          </CTAText>
          <ConsultingCTA href={`${CALCOM_URL}/discovery-call`} target="_blank" rel="noopener noreferrer">
            Schedule a Call <FiArrowRight />
          </ConsultingCTA>
        </CTACard>
      </Container>

      {/* Contact Info Edit Modal */}
      {showContactModal && (
        <EditableModal
          isOpen={showContactModal}
          isCreating={isCreatingContact}
          onClose={cancelContactEdit}
          onSave={saveContact}
          title={isCreatingContact ? 'Add Contact Info' : 'Edit Contact Info'}
        >
          <FormGroup>
            <Label>Title</Label>
            <Input
              type="text"
              value={contactForm.title || ''}
              onChange={e => updateContactForm({ title: e.target.value })}
              placeholder="Contact Information"
            />
          </FormGroup>

          <FormGroup>
            <Label>Description</Label>
            <RichTextEditor
              value={contactForm.description || ''}
              onChange={(content) => updateContactForm({ description: content })}
              placeholder="Feel free to reach out..."
              height="120px"
            />
          </FormGroup>

          <FormGroup>
            <Label>Email</Label>
            <Input
              type="email"
              value={contactForm.data?.email || ''}
              onChange={e => updateContactForm({ 
                data: { ...contactForm.data, email: e.target.value }
              })}
              placeholder="your.email@example.com"
            />
          </FormGroup>

          <FormGroup>
            <Label>Location</Label>
            <Input
              type="text"
              value={contactForm.data?.location || ''}
              onChange={e => updateContactForm({ 
                data: { ...contactForm.data, location: e.target.value }
              })}
              placeholder="City, State / Country"
            />
          </FormGroup>
        </EditableModal>
      )}
    </ContactContainer>
  );
};

const ContactContainer = styled.div`
  min-height: 100vh;
  padding: 5rem 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: 5rem;
  padding: 2rem 0;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.5rem 1.25rem;
  background: rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--accent-primary);
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  line-height: 1.15;
  margin-bottom: 1.5rem;
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.25rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  color: var(--text-secondary);
  max-width: 650px;
  margin: 0 auto 2rem;
`;

const ConsultingCTA = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 2rem;
  background: var(--accent-gradient);
  color: white;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1.05rem;
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }
`;

const Section = styled.section`
  margin-bottom: 5rem;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 2.5rem;
  color: var(--text-primary);
  text-align: center;
`;


const ExpertiseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ExpertiseCard = styled.div`
  padding: 1.5rem;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: var(--shadow-md);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-xl);
    border-color: var(--accent-primary);
  }
`;

const ExpertiseIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 12px;
  color: var(--accent-primary);
  margin-bottom: 1rem;
`;

const ExpertiseTitle = styled.h3`
  font-size: 1.1rem;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const ExpertiseDesc = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const CTACard = styled.section`
  text-align: center;
  padding: 3rem 2rem;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  margin-bottom: 3rem;
`;

const CTAText = styled.p`
  color: var(--text-secondary);
  font-size: 1.1rem;
  max-width: 550px;
  margin: 0 auto 2rem;
  line-height: 1.6;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 4rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ContactInfo = styled.div``;

const ContactMethodLink = styled.a`
  display: flex;
  gap: 1.5rem;
  align-items: center;
  text-decoration: none;
  padding: 1rem;
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(102, 126, 234, 0.05);
    transform: translateX(4px);
  }
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const IconWrapper = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: var(--accent-gradient);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const InfoLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1.125rem;
`;

const ContactForm = styled.form`
  background: var(--bg-secondary);
  padding: 2.5rem;
  border-radius: 1rem;
  box-shadow: var(--shadow-lg);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  background: var(--bg-primary);
  border: 2px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.875rem 1rem;
  background: var(--bg-primary);
  border: 2px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem 2rem;
  background: var(--accent-gradient);
  color: white;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }
`;


export default Contact;
